// Cricket Dada — Offline Storage (IndexedDB)
// Stores: syncQueue (ops to replay), dataCache (API responses), authCache (offline login)

const DB_NAME = 'cricket-dada-offline';
const DB_VER = 2;

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VER);
    req.onupgradeneeded = e => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains('syncQueue')) {
        const store = db.createObjectStore('syncQueue', { keyPath: 'id', autoIncrement: true });
        store.createIndex('matchId', 'matchId');
      }
      if (!db.objectStoreNames.contains('dataCache')) {
        db.createObjectStore('dataCache', { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains('authCache')) {
        db.createObjectStore('authCache', { keyPath: 'id' });
      }
    };
    req.onsuccess = e => resolve(e.target.result);
    req.onerror = () => reject(req.error);
  });
}

// ── Sync Queue ────────────────────────────────────────────────────────────────

export async function queueOp(matchId, type, payload) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('syncQueue', 'readwrite');
    const req = tx.objectStore('syncQueue').add({ matchId, type, payload, ts: Date.now() });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function getPendingOps(matchId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('syncQueue', 'readonly');
    const req = tx.objectStore('syncQueue').index('matchId').getAll(matchId);
    req.onsuccess = () => resolve(req.result.sort((a, b) => a.id - b.id));
    req.onerror = () => reject(req.error);
  });
}

export async function removeOp(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('syncQueue', 'readwrite');
    const req = tx.objectStore('syncQueue').delete(id);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function popLastOp(matchId) {
  const ops = await getPendingOps(matchId);
  if (!ops.length) return null;
  const last = ops[ops.length - 1];
  await removeOp(last.id);
  return last;
}

export async function countPendingOps(matchId) {
  const ops = await getPendingOps(matchId);
  return ops.length;
}

// ── Data Cache ────────────────────────────────────────────────────────────────

export async function cacheData(key, data) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('dataCache', 'readwrite');
    const req = tx.objectStore('dataCache').put({ key, data, ts: Date.now() });
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

// Returns { key, data, ts } or null
export async function getCached(key) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('dataCache', 'readonly');
    const req = tx.objectStore('dataCache').get(key);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}

export async function clearAllCache() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('dataCache', 'readwrite');
    const req = tx.objectStore('dataCache').clear();
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

// ── Auth Cache (offline login) ────────────────────────────────────────────────

export async function hashPassword(password) {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(password));
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
}

export async function cacheAuth(email, password, user, token) {
  const passwordHash = await hashPassword(password);
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('authCache', 'readwrite');
    const req = tx.objectStore('authCache').put({ id: 'current', email, passwordHash, user, token, ts: Date.now() });
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function getAuth() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('authCache', 'readonly');
    const req = tx.objectStore('authCache').get('current');
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}

export async function clearAuth() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('authCache', 'readwrite');
    const req = tx.objectStore('authCache').delete('current');
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}
